import { FC, useMemo, useState } from 'react';

import { GiftOrderForAdmin } from '@shared/api/giftOrder/giftOrder.interface';

import {
  BAR_COLORS,
  BAR_HEX,
  OrderWithDiff,
  buildBins,
  fmt,
  getNiceScale,
  getOrderWithDiff,
} from './analysisUtils';

interface Props {
  orderList: GiftOrderForAdmin[] | null;
}

export const TimingTab: FC<Props> = function TimingTab({ orderList }) {
  const [revHover, setRevHover] = useState<{
    idx: number;
    x: number;
    y: number;
  } | null>(null);

  const timingData = useMemo(() => {
    if (!orderList) return null;

    const items: OrderWithDiff[] = [];
    let skippedCount = 0;
    let beforeFuneralCount = 0;

    for (const order of orderList) {
      const item = getOrderWithDiff(order);
      if (item) {
        if (item.dayDiff < 0) {
          beforeFuneralCount++;
        } else {
          items.push(item);
        }
      } else {
        skippedCount++;
      }
    }

    if (items.length === 0) {
      return { bins: [], stats: null, skippedCount, beforeFuneralCount };
    }

    const diffs = items.map((i) => i.dayDiff).sort((a, b) => a - b);
    const sum = diffs.reduce((a, b) => a + b, 0);
    const avg = sum / diffs.length;
    const median =
      diffs.length % 2 === 0
        ? ((diffs[diffs.length / 2 - 1] ?? 0) + (diffs[diffs.length / 2] ?? 0)) /
          2
        : (diffs[Math.floor(diffs.length / 2)] ?? 0);

    const totalRevenue = items.reduce((s, i) => s + i.revenue, 0);
    const totalQuantity = items.reduce((s, i) => s + i.quantity, 0);
    const bins = buildBins(items);

    return {
      bins,
      stats: {
        count: items.length,
        avg,
        median,
        totalRevenue,
        avgRevenue: Math.round(totalRevenue / items.length),
        totalQuantity,
      },
      skippedCount,
      beforeFuneralCount,
    };
  }, [orderList]);

  if (!timingData || !timingData.stats) {
    return (
      <div className="rounded-lg bg-white p-8 text-center shadow">
        <p className="text-gray-500">
          {timingData &&
          (timingData.skippedCount > 0 || timingData.beforeFuneralCount > 0)
            ? '발인일 또는 구매일 데이터가 없는 주문만 존재합니다.'
            : '해당 기간에 주문이 없습니다.'}
        </p>
      </div>
    );
  }

  const { bins, stats, skippedCount, beforeFuneralCount } = timingData;
  const maxCount = Math.max(...bins.map((b) => b.count), 1);
  const countScale = getNiceScale(maxCount);
  const maxAvgRevenue = Math.max(...bins.map((b) => b.avgRevenue), 1);

  // 매출 구성비 도넛: 당일~4~7일후는 그대로, 8일후 이후는 하나로 합산
  const REVENUE_HEAD = 5;
  const revenueGroups = [
    ...bins.slice(0, REVENUE_HEAD),
    bins.slice(REVENUE_HEAD).reduce(
      (acc, b) => ({
        label: '8일후~',
        revenue: acc.revenue + b.revenue,
        revenueShare: acc.revenueShare + b.revenueShare,
      }),
      { label: '8일후~', revenue: 0, revenueShare: 0 },
    ),
  ].map((g) => ({
    label: g.label,
    revenue: g.revenue,
    revenueShare: g.revenueShare,
  }));

  return (
    <div className="space-y-6">
      {/* 핵심 지표 */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">분석 대상</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {fmt(stats.count)}건
          </p>
          {(skippedCount > 0 || beforeFuneralCount > 0) && (
            <p className="mt-1 text-xs text-gray-400">
              {beforeFuneralCount > 0 && `발인전 ${beforeFuneralCount}건`}
              {beforeFuneralCount > 0 && skippedCount > 0 && ' · '}
              {skippedCount > 0 && `데이터부족 ${skippedCount}건`}
              {' 제외'}
            </p>
          )}
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">총 매출</p>
          <p className="mt-1 text-2xl font-bold text-green-600">
            {fmt(stats.totalRevenue)}원
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">평균 객단가</p>
          <p className="mt-1 text-2xl font-bold text-indigo-600">
            {fmt(stats.avgRevenue)}원
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">구매 시점 평균</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            발인 후 {stats.avg.toFixed(1)}일
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">구매 시점 중앙값</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            발인 후 {stats.median}일
          </p>
        </div>
      </div>

      {/* 주문 건수 분포 + 매출 구성비 */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* 수직 막대 */}
        <div className="flex flex-col rounded-lg bg-white p-6 shadow">
          <h2 className="mb-1 text-lg font-bold text-gray-800">
            주문 건수 분포
          </h2>
          <p className="mb-6 text-xs text-gray-400">
            발인 당일부터 시간이 지남에 따른 주문 건수
          </p>
          <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col">
            <div className="flex min-h-[240px] flex-1">
              {/* Y축 눈금 */}
              <div className="relative mr-2 shrink-0" style={{ width: 32 }}>
                {countScale.ticks.map((t) => (
                  <span
                    key={t}
                    className="absolute right-0 translate-y-1/2 text-[10px] leading-none text-gray-400"
                    style={{ bottom: `${(t / countScale.niceMax) * 100}%` }}
                  >
                    {fmt(t)}
                  </span>
                ))}
              </div>
              {/* 플롯 영역 */}
              <div className="relative flex-1">
                {/* 가이드 선 */}
                {countScale.ticks.map((t) => (
                  <div
                    key={t}
                    className="pointer-events-none absolute inset-x-0 border-t border-gray-100"
                    style={{ bottom: `${(t / countScale.niceMax) * 100}%` }}
                  />
                ))}
                {/* 막대 */}
                <div className="absolute inset-0 flex items-end justify-around">
                  {bins.map((bin, i) => {
                    const pct =
                      countScale.niceMax > 0
                        ? (bin.count / countScale.niceMax) * 100
                        : 0;
                    return (
                      <div
                        key={bin.label}
                        className="group relative flex h-full flex-col items-center justify-end"
                        style={{ flex: 1 }}
                      >
                        <div className="pointer-events-none absolute -top-1 z-10 hidden -translate-y-full rounded bg-gray-800 px-2 py-1 text-[10px] leading-tight whitespace-nowrap text-white shadow group-hover:block">
                          {bin.label}: {fmt(bin.count)}건 · {fmt(bin.revenue)}원
                        </div>
                        <span className="mb-1 text-[11px] font-bold text-gray-700">
                          {bin.count > 0 ? bin.count : ''}
                        </span>
                        <div
                          className={`w-full max-w-[56px] rounded-t transition-all duration-500 ${BAR_COLORS[i] ?? 'bg-gray-300'}`}
                          style={{
                            height: `${pct}%`,
                            minHeight: bin.count > 0 ? 4 : 0,
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            {/* X축 라벨 */}
            <div className="flex">
              <div className="mr-2 shrink-0" style={{ width: 32 }} />
              <div className="flex flex-1 justify-around">
                {bins.map((bin) => (
                  <span
                    key={bin.label}
                    className="mt-2 text-center text-[11px] font-medium text-gray-500"
                    style={{ flex: 1 }}
                  >
                    {bin.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 매출 구성비 */}
        <div className="flex flex-col rounded-lg bg-white p-6 shadow">
          <h2 className="mb-1 text-lg font-bold text-gray-800">매출 구성비</h2>
          <p className="mb-4 text-xs text-gray-400">
            각 구간이 전체 매출에서 차지하는 비중 (8일후~ 합산)
          </p>
          <div className="flex flex-1 flex-col items-center justify-center gap-6">
            {/* 도넛 */}
            <div
              className="relative shrink-0"
              style={{ width: 200, height: 200 }}
            >
              <svg viewBox="0 0 200 200" className="h-full w-full -rotate-90">
                {(() => {
                  const r = 72;
                  const c = 2 * Math.PI * r;
                  let acc = 0;
                  return revenueGroups.map((g, i) => {
                    if (g.revenueShare <= 0) return null;
                    const seg = (g.revenueShare / 100) * c;
                    const offset = -(acc / 100) * c;
                    acc += g.revenueShare;
                    return (
                      <circle
                        key={g.label}
                        cx={100}
                        cy={100}
                        r={r}
                        fill="none"
                        stroke={BAR_HEX[i] ?? '#cbd5e1'}
                        strokeWidth={28}
                        strokeDasharray={`${seg} ${c}`}
                        strokeDashoffset={offset}
                        className="cursor-pointer transition-opacity hover:opacity-80"
                        onMouseMove={(e) => {
                          const svg = e.currentTarget.ownerSVGElement;
                          if (!svg) return;
                          const rect = svg.getBoundingClientRect();
                          setRevHover({
                            idx: i,
                            x: e.clientX - rect.left,
                            y: e.clientY - rect.top,
                          });
                        }}
                        onMouseLeave={() => setRevHover(null)}
                      />
                    );
                  });
                })()}
              </svg>
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-[11px] text-gray-400">총 매출</span>
                <span className="text-lg font-bold text-gray-900">
                  {fmt(stats.totalRevenue)}원
                </span>
              </div>
              {revHover && revenueGroups[revHover.idx] && (
                <div
                  className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded bg-gray-800 px-2 py-1 text-[11px] leading-tight whitespace-nowrap text-white shadow"
                  style={{ left: revHover.x, top: revHover.y - 8 }}
                >
                  {revenueGroups[revHover.idx]!.label}{' '}
                  {revenueGroups[revHover.idx]!.revenueShare.toFixed(1)}%
                  {' · '}
                  {fmt(revenueGroups[revHover.idx]!.revenue)}원
                </div>
              )}
            </div>
            {/* 범례 */}
            <div className="w-full max-w-sm space-y-2">
              {revenueGroups.map((g, i) =>
                g.revenue > 0 ? (
                  <div
                    key={g.label}
                    className="flex items-center gap-2 text-sm"
                  >
                    <span
                      className="inline-block h-3 w-3 shrink-0 rounded-sm"
                      style={{ backgroundColor: BAR_HEX[i] ?? '#cbd5e1' }}
                    />
                    <span className="w-16 shrink-0 text-gray-600">
                      {g.label}
                    </span>
                    <span className="flex-1 text-right font-bold text-gray-800">
                      {g.revenueShare.toFixed(1)}%
                    </span>
                    <span className="w-28 shrink-0 text-right text-green-600">
                      {fmt(g.revenue)}원
                    </span>
                  </div>
                ) : null,
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 객단가 + 건당수량 */}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="mb-1 text-lg font-bold text-gray-800">
            구간별 객단가
          </h2>
          <p className="mb-4 text-xs text-gray-400">
            고객단가 구간 = 마케팅 고가치 타겟
          </p>
          <div className="space-y-2.5">
            {bins.map((bin, i) => {
              const pct =
                maxAvgRevenue > 0 ? (bin.avgRevenue / maxAvgRevenue) * 100 : 0;
              return (
                <div key={bin.label} className="flex items-center gap-2">
                  <span className="w-16 shrink-0 text-right text-xs font-medium text-gray-600">
                    {bin.label}
                  </span>
                  <div className="relative h-6 flex-1 rounded bg-gray-50">
                    {bin.avgRevenue > 0 && (
                      <div
                        className={`h-full rounded transition-all duration-500 ${BAR_COLORS[i] ?? 'bg-gray-300'}`}
                        style={{
                          width: `${Math.max(pct, 2)}%`,
                          opacity: 0.7,
                        }}
                      />
                    )}
                  </div>
                  <span className="w-20 shrink-0 text-right text-xs font-bold text-gray-700">
                    {bin.avgRevenue > 0 ? `${fmt(bin.avgRevenue)}원` : '-'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="mb-1 text-lg font-bold text-gray-800">
            구간별 건당 수량
          </h2>
          <p className="mb-4 text-xs text-gray-400">
            대량 구매 구간 = 단체 주문 타겟
          </p>
          <div className="space-y-2.5">
            {bins.map((bin, i) => {
              const maxQ = Math.max(...bins.map((b) => b.avgQuantity), 1);
              const pct = maxQ > 0 ? (bin.avgQuantity / maxQ) * 100 : 0;
              return (
                <div key={bin.label} className="flex items-center gap-2">
                  <span className="w-16 shrink-0 text-right text-xs font-medium text-gray-600">
                    {bin.label}
                  </span>
                  <div className="relative h-6 flex-1 rounded bg-gray-50">
                    {bin.avgQuantity > 0 && (
                      <div
                        className={`h-full rounded transition-all duration-500 ${BAR_COLORS[i] ?? 'bg-gray-300'}`}
                        style={{
                          width: `${Math.max(pct, 2)}%`,
                          opacity: 0.7,
                        }}
                      />
                    )}
                  </div>
                  <span className="w-16 shrink-0 text-right text-xs font-bold text-gray-700">
                    {bin.avgQuantity > 0
                      ? `${bin.avgQuantity.toFixed(1)}개`
                      : '-'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 상세 테이블 */}
      <div className="rounded-lg bg-white p-6 shadow">
        <h2 className="mb-4 text-lg font-bold text-gray-800">구간별 상세</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs font-medium text-gray-500">
                <th className="pb-2 pr-4">구간</th>
                <th className="pb-2 pr-4 text-right">건수</th>
                <th className="pb-2 pr-4 text-right">비중</th>
                <th className="pb-2 pr-4 text-right">누적</th>
                <th className="pb-2 pr-4 text-right">매출</th>
                <th className="pb-2 pr-4 text-right">매출비중</th>
                <th className="pb-2 pr-4 text-right">객단가</th>
                <th className="pb-2 pr-4 text-right">수량</th>
                <th className="pb-2 text-right">건당 수량</th>
              </tr>
            </thead>
            <tbody>
              {bins.map((bin, i) => {
                const share =
                  stats.count > 0 ? (bin.count / stats.count) * 100 : 0;
                return (
                  <tr
                    key={bin.label}
                    className="border-b border-gray-100 last:border-0"
                  >
                    <td className="py-2 pr-4 font-medium text-gray-700">
                      <span className="flex items-center gap-2">
                        <span
                          className={`inline-block h-2.5 w-2.5 rounded-sm ${BAR_COLORS[i] ?? 'bg-gray-300'}`}
                        />
                        {bin.label}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-right font-bold text-gray-900">
                      {fmt(bin.count)}
                    </td>
                    <td className="py-2 pr-4 text-right text-gray-600">
                      {share.toFixed(1)}%
                    </td>
                    <td className="py-2 pr-4 text-right font-medium text-indigo-600">
                      {bin.cumRate.toFixed(1)}%
                    </td>
                    <td className="py-2 pr-4 text-right text-green-600">
                      {fmt(bin.revenue)}원
                    </td>
                    <td className="py-2 pr-4 text-right text-green-600">
                      {bin.revenueShare.toFixed(1)}%
                    </td>
                    <td className="py-2 pr-4 text-right font-bold text-indigo-600">
                      {bin.avgRevenue > 0 ? `${fmt(bin.avgRevenue)}원` : '-'}
                    </td>
                    <td className="py-2 pr-4 text-right text-gray-600">
                      {fmt(bin.quantity)}개
                    </td>
                    <td className="py-2 text-right text-gray-600">
                      {bin.avgQuantity > 0
                        ? `${bin.avgQuantity.toFixed(1)}개`
                        : '-'}
                    </td>
                  </tr>
                );
              })}
              <tr className="border-t-2 border-gray-300 font-bold">
                <td className="py-2 pr-4 text-gray-700">합계</td>
                <td className="py-2 pr-4 text-right text-gray-900">
                  {fmt(stats.count)}
                </td>
                <td className="py-2 pr-4 text-right text-gray-600">100%</td>
                <td className="py-2 pr-4 text-right text-indigo-600">-</td>
                <td className="py-2 pr-4 text-right text-green-600">
                  {fmt(stats.totalRevenue)}원
                </td>
                <td className="py-2 pr-4 text-right text-green-600">100%</td>
                <td className="py-2 pr-4 text-right text-indigo-600">
                  {fmt(stats.avgRevenue)}원
                </td>
                <td className="py-2 pr-4 text-right text-gray-600">
                  {fmt(stats.totalQuantity)}개
                </td>
                <td className="py-2 text-right text-gray-600">
                  {(stats.totalQuantity / stats.count).toFixed(1)}개
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
